import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Spinner,
  Image,
  Grid,
  GridItem,
  Button,
  useToast,
} from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { SlBasket } from "react-icons/sl";
import { useAuth } from "../../hooks/useAuth";
import axiosInstance from "../../services/axios";
import { useCart } from "../../context/CartContext";
import EarningsService from "../../services/earningsService";

const Portfolio = () => {
  const auth = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [tracks, setTracks] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [trackEarnings, setTrackEarnings] = useState(new Map());
  const [loading, setLoading] = useState(true); // Single loading state

  // 1) Fetch portfolio & tracks in one go
  const fetchUserPortfolioAndTracks = async () => {
    setLoading(true);
    try {
      // --- Fetch the user (to get portfolio) ---
      const userRes = await axiosInstance.get("/users/me");
      const userPortfolio = userRes.data.account.profile.portfolio;
      setPortfolio(userPortfolio);

      // --- If portfolio is empty or null, no need to fetch tracks ---
      if (!userPortfolio || userPortfolio.length === 0) {
        setTracks([]);
        return;
      }

      // --- Fetch tracks in parallel ---
      const trackPromises = userPortfolio.map((track) =>
        axiosInstance.post(`/music/get_track_by_id/${track.track_id}`)
      );
      const responses = await Promise.all(trackPromises);

      // --- Combine track data with date/quantity from portfolio ---
      const fetchedTracks = responses.map((response, i) => ({
        track_data: response.data,
        date_acquired: userPortfolio[i].date_acquired,
        quantity_bought: userPortfolio[i].num_of_shares,
      }));
      setTracks(fetchedTracks);

      // --- Fetch earnings data for tracks with ISRC ---
      const tracksWithISRC = fetchedTracks.filter(
        (track) => track.track_data.isrc
      );
      if (tracksWithISRC.length > 0) {
        const isrcList = tracksWithISRC.map((track) => track.track_data.isrc);
        const earningsMap = await EarningsService.getMultipleTrackEarnings(
          isrcList
        );
        setTrackEarnings(earningsMap);
      }
    } catch (error) {
      console.error("Error fetching portfolio or tracks:", error);
      toast({
        title: "Error",
        description:
          "Failed to fetch portfolio/tracks. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate user's projected earnings
  const calculateUserProjectedEarnings = (track, earnings) => {
    if (!earnings || !track.track_data.total_shares || !track.quantity_bought) {
      return 0;
    }

    return EarningsService.calculateUserProjectedEarnings(
      earnings.projected_month_earnings,
      track.quantity_bought,
      track.track_data.total_shares
    );
  };

  // Redirect linked artists (in switched context) away from portfolio
  useEffect(() => {
    if (auth.isSwitchedContext) {
      toast({
        title: 'Portfolio Not Available',
        description: 'Linked artists do not have access to portfolio.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      navigate('/dashboard');
    }
  }, [auth.isSwitchedContext, navigate, toast]);

  // Run once on mount
  useEffect(() => {
    fetchUserPortfolioAndTracks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Spinner while loading everything
  if (loading) {
    return (
      <Flex justify="center" align="center">
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  }

  // If portfolio is null or empty after loading
  if (portfolio === null || portfolio.length === 0) {
    return (
      <Flex
        justify="space-between"
        align={{ base: "flex-start", lg: "center" }}
        flexDirection={{ base: "column", lg: "row" }}
      >
        <Box p={4}>
          <Button
            colorScheme="blue"
            onClick={() => navigate("/dashboard/billing")}
          >
            Transactions
          </Button>
        </Box>
        <Text fontSize="lg" color="primary.100">
          No streamshares. Let's buy and earn from your favorite music!
        </Text>
      </Flex>
    );
  }

  // Otherwise, display the portfolio
  return (
    <Box color="primary.100">
      <Flex
        justify="space-between"
        align={{ base: "flex-start", lg: "center" }}
        flexDirection={{ base: "column", lg: "row" }}
      >
        <Heading as="h3" size="lg" mb={4} color="dark.900">
          Portfolio
        </Heading>
        <Text>
          <Text as="span">Total Earnings : </Text> $
          {auth.user.account.profile.earnings}
        </Text>
        <Box py={4}>
          <Button
            colorScheme="blue"
            onClick={() => navigate("/dashboard/billing")}
          >
            Transactions
          </Button>
        </Box>
      </Flex>

      {/* Table header */}
      <Grid
        templateColumns={{
          base: "1fr 3fr",
          md: "2.5fr 1fr 1fr 1fr 1fr 1fr 1fr",
        }}
        gap={3}
        p={2}
        templateRows={{ base: "1fr 1fr", sm: "1fr" }}
        align="center"
        justifyItems="flex-start"
        bg="secondary.900"
        color="primary.100"
        borderRadius="md"
        boxShadow="md"
        display={{ base: "none", md: "grid" }}
      >
        <GridItem>
          <Text fontSize="lg">Track</Text>
        </GridItem>
        <GridItem justifySelf="center">
          <Text fontSize="lg">streamshares</Text>
        </GridItem>
        <GridItem justifySelf="center">
          <Text fontSize="lg">Total Earnings</Text>
        </GridItem>
        <GridItem justifySelf="center">
          <Text fontSize="lg">Projected</Text>
        </GridItem>
        <GridItem justifySelf="center">
          <Text fontSize="lg">Tx Hash</Text>
        </GridItem>
        <GridItem>
          <Text fontSize="lg">Price</Text>
        </GridItem>
        <GridItem>
          <Text fontSize="lg">Buy More</Text>
        </GridItem>
      </Grid>

      {/* Tracks listing */}
      <Box>
        {tracks.map((item, index) => {
          const trackEarningsData = trackEarnings.get(item.track_data.isrc);
          const userProjectedEarnings = calculateUserProjectedEarnings(
            item,
            trackEarningsData
          );
          const projectedEarningsColor =
            EarningsService.getProjectedEarningsColor(
              userProjectedEarnings,
              trackEarningsData,
              item.quantity_bought,
              item.track_data.total_shares
            );

          return (
            <Grid
              as={Link}
              to={`/track/${item.track_data.track_id}`}
              key={index}
              templateColumns={{
                base: "1fr 3fr",
                md: "2.5fr 1fr 1fr 1fr 1fr 1fr 1fr",
              }}
              templateRows={{ base: "2fr 1fr 1fr", md: "1fr" }}
              gap={{ base: 3, md: 3 }}
              p={{ base: 4, md: 2 }}
              align={{ base: "left", md: "center" }}
              alignItems="center"
              justifyItems={{ base: "space-between", md: "flex-start" }}
              bg="secondary.100"
              borderRadius="md"
              boxShadow="md"
              color="primary.900"
              my={3}
              _hover={{ bg: "secondary.500" }}
            >
              {/* Track Image & Title */}
              <GridItem
                gridColumn={{ base: "1/2", md: "1/2" }}
                gridRow={{ base: "1/4", md: "1/3" }}
                w={{ base: "100%", md: "100%" }}
                display="flex"
                alignItems="center"
                justifySelf="flex-start"
              >
                <Image
                  src={item.track_data.image_url}
                  alt={item.track_data.track_name}
                  borderRadius="lg"
                  w="4rem"
                />
                <Text
                  display={{ base: "none", md: "block" }}
                  ml={{ base: 1, md: 4 }}
                >
                  {item.track_data.track_name}
                </Text>
              </GridItem>

              {/* Number of shares (desktop only) */}
              <GridItem
                justifySelf="center"
                gridColumn="2/3"
                display={{ base: "none", md: "block" }}
                gridRow="1/3"
              >
                <Text fontSize="lg">{item.quantity_bought}</Text>
              </GridItem>

              {/* Earnings (desktop only) */}
              <GridItem
                justifySelf="center"
                gridColumn="3/4"
                display={{ base: "none", md: "block" }}
                gridRow="1/3"
                alignSelf="center"
              >
                <Text fontSize="lg">${portfolio[index].balance}</Text>
              </GridItem>

              {/* Projected Earnings (desktop only) */}
              <GridItem
                justifySelf="center"
                gridColumn="4/5"
                display={{ base: "none", md: "block" }}
                gridRow="1/3"
                alignSelf="center"
              >
                <Text
                  fontSize="sm"
                  color={projectedEarningsColor}
                  fontWeight="semibold"
                >
                  {EarningsService.formatEarnings(userProjectedEarnings)}
                </Text>
              </GridItem>

              {/* NFT Transaction Hash (desktop only) */}
              <GridItem
                justifySelf="center"
                gridColumn="5/6"
                display={{ base: "none", md: "block" }}
                gridRow="1/3"
                alignSelf="center"
              >
                {portfolio[index].nft_transaction_hash ? (
                  <Text
                    as="a"
                    href={`https://polygonscan.com/tx/${portfolio[index].nft_transaction_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    fontSize="xs"
                    color="blue.400"
                    fontWeight="bold"
                    title={`View transaction on PolygonScan: ${portfolio[index].nft_transaction_hash}`}
                    _hover={{ color: "blue.300", textDecoration: "underline" }}
                    cursor="pointer"
                  >
                    {`${portfolio[index].nft_transaction_hash.substring(
                      0,
                      6
                    )}...${portfolio[index].nft_transaction_hash.slice(-4)}`}
                  </Text>
                ) : (
                  <Text fontSize="xs" color="gray.400">
                    N/A
                  </Text>
                )}
              </GridItem>

              {/* Price per share */}
              <GridItem
                gridColumn={{ base: "2/3", md: "6/7" }}
                gridRow="1/3"
                alignSelf={{ base: "flex-end", md: "center" }}
              >
                <Text fontSize={{ base: "lg", md: "lg" }}>
                  ${item.track_data.price_per_share}
                </Text>
              </GridItem>

              {/* Buy More + Mobile fallback displays */}
              <GridItem
                gridColumn={{ base: "1/3", md: "7/8" }}
                gridRow={{ base: "4/5", md: "1/3" }}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Box>
                  {/* Mobile Only: show shares & earnings & projected earnings & Transaction Hash */}
                  <Text display={{ base: "block", md: "none" }}>
                    streamshares : {item.quantity_bought}
                  </Text>
                  <Text display={{ base: "block", md: "none" }}>
                    total earnings : ${portfolio[index].balance}
                  </Text>
                  <Text
                    display={{ base: "block", md: "none" }}
                    color={projectedEarningsColor}
                  >
                    projected :{" "}
                    {EarningsService.formatEarnings(userProjectedEarnings)}
                  </Text>
                  <Box display={{ base: "block", md: "none" }}>
                    <Text as="span" fontSize="xs" color="gray.400">
                      Tx Hash :{" "}
                    </Text>
                    {portfolio[index].nft_transaction_hash ? (
                      <Text
                        as="a"
                        href={`https://polygonscan.com/tx/${portfolio[index].nft_transaction_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontSize="xs"
                        color="blue.400"
                        title={`View transaction on PolygonScan: ${portfolio[index].nft_transaction_hash}`}
                        _hover={{
                          color: "blue.300",
                          textDecoration: "underline",
                        }}
                        cursor="pointer"
                      >
                        {`${portfolio[index].nft_transaction_hash.substring(
                          0,
                          8
                        )}...${portfolio[index].nft_transaction_hash.slice(
                          -6
                        )}`}
                      </Text>
                    ) : (
                      <Text as="span" fontSize="xs" color="gray.400">
                        N/A
                      </Text>
                    )}
                  </Box>
                </Box>

                <Button
                  my={2}
                  leftIcon={<SlBasket />}
                  onClick={(e) => {
                    e.preventDefault();
                    addToCart(item.track_data);
                  }}
                  variant="glassy"
                >
                  Buy More
                </Button>
              </GridItem>
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
};

export default Portfolio;
